
import { createRef, Component } from 'react'
import { ImageURLs, ImageSource } from 'interfaces/media'
import { findImage } from 'helpers/mediaHelpers'
import * as debounce from 'lodash/debounce'

export type AdjustableImageContentProps = {
  image?: ImageURLs | null
  className?: string
  onImageFound?: (image: ImageSource) => void
}

type AdjustableImageContentState = {
  adjustedImage: ImageSource | null
  width: number | null
  prevImage: ImageURLs | null
}

export default class AdjustableImageContent extends Component<AdjustableImageContentProps, AdjustableImageContentState> {
  static defaultProps = {
    preventBackground: false,
  }

  state: AdjustableImageContentState = {
    adjustedImage: null,
    width: null,
    prevImage: null,
  }

  private imageRef = createRef<HTMLDivElement>()
  private get imageNode () {
    if (!this.imageRef.current) {
      throw new Error('container not found')
    }
    return this.imageRef.current
  }

  private containerMutationObserver: MutationObserver

  render () {
    const { adjustedImage } = this.state
    const { className } = this.props

    return (
      <div
        ref={ this.imageRef }
        className={ className }
        style={{
          backgroundImage: adjustedImage ? `url(${ adjustedImage.url })` : undefined,
        }}
      />
    )
  }

  componentDidMount () {
    this.containerMutationObserver = new MutationObserver(this.handleResize)
    this.containerMutationObserver.observe(this.imageNode, { attributes: true })
    window.addEventListener('resize', this.handleResize)
    this.update()
  }

  componentDidUpdate () {
    this.update()
  }

  componentWillUnmount () {
    this.containerMutationObserver.disconnect()
    window.removeEventListener('resize', this.handleResize)
    this.handleResize.cancel()
  }

  private update () {
    const { clientWidth } = this.imageNode
    const { width, prevImage } = this.state
    const { image, onImageFound } = this.props
    const isNewWidth = clientWidth !== width
    const isNewImage = image !== prevImage

    if (!image) {
      return
    }

    if (!isNewWidth && !isNewImage) {
      return
    }

    const adjustedImage = findImage(image, clientWidth)

    this.setState({
      width: clientWidth,
      adjustedImage,
      prevImage: image,
    })

    if (onImageFound) {
      onImageFound(adjustedImage)
    }
  }

  private handleResize = debounce(() => this.update(), 50)
}
